import { BOSSES } from './bosses.js';
import { WAVE_RAMP } from '../../config/constants.js';

/**
 * The Undersong Tide (BULLET-HEAVEN.md §H7) — the quarantine.
 *
 * A true 20-minute bullet heaven, built as a MODE rather than as a
 * replacement. That distinction is the whole design: Survivor's identity is
 * the boss gauntlet, and every one of the machines the plan imports is
 * deliberately kept out of the fights where telegraph-reading, graze, dash
 * i-frames and the mercy window live. The Tide is where the genre's own shape
 * gets to exist undiluted, without any of it leaking into a boss.
 *
 * One arena. No gauntlet. A continuous rising spawn curve for twenty minutes,
 * a reaper when the clock runs out, and XP motes as the only progression.
 *
 * It is mostly free precisely because H1 and H2 built its parts: it reuses
 * MinionWaveSystem's ramp, CombatSystem, ProgressionSystem, PickupSystem and
 * every champion and weapon WITHOUT MODIFICATION. What it actually is, in
 * code, is a different stage graph and this file's worth of numbers.
 *
 * Two things it buys beyond the fantasy:
 *  - It is the natural home for uncapped Echo income (ARCHIVE.md gap 1), because
 *    a mode with no boss-kill milestones needs a different payout shape anyway.
 *  - It is a far better first run than a cold open into a boss gauntlet: a new
 *    player learns movement, motes, cards and weapons with no telegraph-reading
 *    required at all.
 */

// Bodies one spawn tick may add, and the multiplier the slot budget is sized
// off. Hoisted out of TIDE because `budgetSlots` is computed from it inside
// the same object literal.
const TIDE_MAX_SPAWN_BURST = 24;

export const TIDE = {
  id: 'undersongTide',
  name: 'Undersong Tide',
  // Twenty minutes, the genre's session length. MinionWaveSystem's ramp reads
  // duration as `waveCount * WAVE_RAMP.secondsPerWaveUnit` (25s), so 48 units
  // is 1200s. Expressed that way rather than in seconds so the Tide rides the
  // exact same curve code a wave stage does, with no special case.
  waveUnits: 48,
  durationSeconds: 48 * 25,
  // Peak concurrent bodies at the twenty-minute mark. The ramp opens at
  // WAVE_RAMP.concurrentStart of this — 0.35 * 140 = 49 — and climbs to the
  // full 140. Enemy density rising to meet the player's power is the machine
  // (§4, pressure inversion): the screen must stay full the entire time, or
  // the run snowballs into an empty arena and spends its own drug.
  //
  // Raised from 40, which was a genre-plausible number measured against the
  // wrong denominator. The arena is 700x500 and the viewport 384x216, so only
  // ~24% of the field is ever visible at once. Chasing minions converge on the
  // player rather than spreading evenly, so that did not cost as much as the
  // raw ratio suggests — a headless run of the real spawner put ~30 of the 40
  // on screen, not the ~10 a uniform distribution would predict. But ~30 is
  // still well under the genre: Vampire Survivors runs a ~300 cap with its
  // whole play field in frame and fills a third to a half of the screen with
  // bodies, and Brotato peaks lower but shows 100% of its arena. The measure
  // that matters is the fraction of the frame covered by enemies, and at 40
  // that was ~4%. 140 puts it near 11%, with ~110 bodies in frame.
  //
  // Authored directly rather than derived, so the Tide sets `crowdScale: false`
  // and keeps its own per-body stats (below) instead of taking the wave
  // stage's automatic softening.
  peakConcurrent: 140,

  // Ceilings on MinionWaveSystem._waveScale's per-step ramp.
  //
  // Those rates (+18% HP, +15% damage per step) were authored for a 4-wave
  // stage, and the Tide feeds the same code 48 units. Uncapped, minute twenty
  // is 9.46x HP and 8.05x contact damage: 246 HP on a skirmisher, and 56
  // damage per touch against a 100 HP player whose 0.4s mercy window then
  // allows ~140 DPS — under a second from full to dead for standing in a
  // crowd. A bullet heaven needs the opposite: individual bodies close to
  // harmless, so that being inside the mob is the intended place to be.
  //
  // Capped, minute twenty is 78 HP and 15 damage a touch. Enemies stop getting
  // stronger around minute five and the mode's back fifteen minutes escalate
  // purely through density — which is the machine, and the only escalation
  // that a snowballing build can actually invert.
  scaleCaps: { hp: 3.0, damage: 2.2, speed: 1.35 },

  /**
   * Act II's deep vocabulary (ACT2-BIBLE.md §8) plus the Act I staples.
   *
   * The deep types are thematically ready-made for this — they are the
   * Undersong's own wave vocabulary, and using them here is what makes the
   * mode read as a place rather than as a settings preset. MinionWaveSystem's
   * ramp introduces them on its own schedule (MID_TYPES/LATE_TYPES), so the
   * first minutes are still Act I's simple shapes.
   *
   * Weighted heavily toward fodder — skirmisher, charger, swarmling and
   * anglerling are ~60% of slots, against ~40% before the density pass. The
   * gauntlet's vocabulary is bullet-HELL vocabulary: a Muffler takes your
   * weapons, a Trapper denies floor, a Splitter trebles itself, and every one
   * of them is a thing you are meant to have time to read and answer. At a
   * peak of 140 bodies there is no such time, and a field made of them is
   * noise rather than a crowd. The interesting types stay in as seasoning —
   * legible precisely because they are rare — and the bulk is bodies whose
   * only job is to be deleted.
   */
  minionTypes: [
    { type: 'skirmisher', weight: 14 },
    { type: 'charger', weight: 10 },
    { type: 'swarmling', weight: 8 }, // one slot, six bodies — the confetti
    { type: 'anglerling', weight: 6 },
    { type: 'caster', weight: 4 },
    { type: 'bomber', weight: 3 },
    { type: 'orbiter', weight: 3 },
    { type: 'mirrorShade', weight: 3 },
    { type: 'leech', weight: 2 },
    { type: 'warden', weight: 2 },
    { type: 'trapper', weight: 2 },
    { type: 'splitter', weight: 2 },
    { type: 'sinkweight', weight: 2 },
    { type: 'muffler', weight: 1 },
  ],

  // Baseline minion stats. Softer than the gauntlet's opening stage because
  // the ramp's own per-stage scale (MinionWaveSystem._waveScale) runs across
  // 48 units here instead of 4 — by minute twenty these are multiplied hard.
  minionHp: 26,
  minionMoveSpeed: 64,
  minionContactDamage: 7,
  minionShotDamage: 4,
  minionShotSpeed: 68,
  minionFireInterval: 2.6,

  // The reaper. When the clock runs out, the mode does not simply stop — it
  // sends something to end you, exactly as the genre does. Reusing the final
  // gauntlet boss's definition rather than authoring a new one is the point:
  // it is the most dangerous thing in the game, and a player who has spent
  // twenty minutes becoming a god should meet it at full strength.
  reaperBossIndex: BOSSES.length - 1,
  reaperHpMultiplier: 1.5,
  // Surviving the reaper is a win. It is meant to be possible only for a build
  // that genuinely snowballed — that is what makes the twenty minutes matter.

  // Spawn-slot budget. A wave stage's budget is deliberately capped at parity
  // with the clear-then-next shape it replaced (§H2) — the Tide has no shape
  // to be at parity WITH, and capping it the same way is actively wrong: at
  // waveUnits * peakConcurrent the arena runs dry around the fifteen-minute
  // mark against any build that actually snowballed, and the mode's last five
  // minutes — the whole payoff — play out in an empty room.
  //
  // Set to the most the spawner could physically place if it fired at its
  // maximum burst on every tick for the full duration, so the budget can never
  // be the binding constraint. The clock is the only thing that ends the Tide.
  budgetSlots: Math.ceil((48 * 25) / WAVE_RAMP.spawnInterval) * TIDE_MAX_SPAWN_BURST,

  // Bodies one spawn tick may add. The shared default (3 per 0.45s, under
  // seven a second) is sized for a wave stage's peak of 8-13, where it never
  // binds. At the Tide's peak of 40 it binds hard: a build that snowballs —
  // which is the entire point of twenty uninterrupted minutes — deletes bodies
  // faster than that, and the arena empties out through the mode's back half.
  //
  // Raised to keep the field able to refill toward its target at any point on
  // the curve. It is still a ceiling, not a firehose: `concurrentTarget` is
  // what decides how full the screen gets, and this only governs how quickly
  // the spawner may close a gap. At a peak of 140 it has to close much larger
  // gaps than it did at 40 — a late-run screen clear now removes a hundred
  // bodies — so it scales with the peak it serves.
  maxSpawnBurst: TIDE_MAX_SPAWN_BURST,

  // Never derives its concurrency from the wave stage's crowd multiplier: the
  // Tide authors `peakConcurrent` and its per-body stats directly, and taking
  // the automatic softening on top would halve numbers that are already
  // deliberate. See WAVE_RAMP.crowdMultiplier.
  crowdScale: false,

  // Echo payout per minute survived. The Tide has no boss-kill milestones, so
  // it pays on the clock instead (ARCHIVE.md gap 1's uncapped income lives here).
  echoesPerMinute: 24,
};

/**
 * The waveConfig the Tide hands MinionWaveSystem. Shaped exactly like a
 * boss's `waveConfig` so the ramp needs no knowledge that this mode exists.
 */
export function tideWaveConfig({ loopMultiplier = 1 } = {}) {
  return {
    rampMode: true, // never the clear-then-next shape — the Tide IS the ramp
    waveCount: TIDE.waveUnits,
    minionsPerWave: TIDE.peakConcurrent,
    crowdScale: TIDE.crowdScale,
    scaleCaps: { ...TIDE.scaleCaps },
    budgetSlots: TIDE.budgetSlots,
    maxSpawnBurst: TIDE.maxSpawnBurst,
    minionTypes: [...TIDE.minionTypes],
    minionHp: TIDE.minionHp * loopMultiplier,
    minionMoveSpeed: TIDE.minionMoveSpeed,
    minionContactDamage: TIDE.minionContactDamage * loopMultiplier,
    minionShotDamage: TIDE.minionShotDamage * loopMultiplier,
    minionShotSpeed: TIDE.minionShotSpeed,
    minionFireInterval: TIDE.minionFireInterval,
  };
}

/** Echoes earned for `seconds` survived in the Tide. */
export function tideEchoes(seconds) {
  return Math.floor((Math.max(0, seconds) / 60) * TIDE.echoesPerMinute);
}
